import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Plus, BarChart3, Users, MousePointer, DollarSign,
  LogOut, Calendar, ChevronRight, Clock, CheckCircle,
  XCircle, AlertCircle, CreditCard, Bell, TrendingUp, X, Archive, ChevronDown, ArrowRightLeft
} from "lucide-react";
import AIHelpAssistant from "../components/AIHelpAssistant";
import LanguageSwitcher from "../components/LanguageSwitcher";

const BusinessDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['dashboard', 'common', 'landing']);
  const { user, logout, setUser } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCampaigns, setArchivedCampaigns] = useState([]);

  // All API calls use httpOnly cookies (withCredentials: true)
  const axiosConfig = { withCredentials: true };

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, campaignsRes, notifRes, archivedRes] = await Promise.all([
        axios.get(`${API}/business/analytics`, axiosConfig),
        axios.get(`${API}/campaigns`, axiosConfig),
        axios.get(`${API}/notifications`, axiosConfig),
        axios.get(`${API}/campaigns?status=cancelled`, axiosConfig).catch(() => ({ data: [] }))
      ]);
      setAnalytics(analyticsRes.data);
      setCampaigns(campaignsRes.data);
      setNotifications(notifRes.data);
      setArchivedCampaigns(archivedRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setNotification({ type: "warning", message: t('common:errors.tryAgain') });
      setPaymentProcessing(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, axiosConfig);
      
      if (response.data.payment_status === "paid") {
        setNotification({ type: "success", message: t('common:messages.success') });
        setPaymentProcessing(false);
        fetchData();
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (response.data.status === "expired") {
        setNotification({ type: "error", message: t('common:errors.somethingWentWrong') });
        setPaymentProcessing(false);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [fetchData, t]);

  useEffect(() => {
    fetchData();

    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (paymentStatus === "success" && sessionId) {
      setPaymentProcessing(true);
      setNotification({ type: "info", message: t('common:actions.loading') });
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      setNotification({ type: "warning", message: t('common:status.cancelled') });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchData, searchParams, pollPaymentStatus, t]);

  const handleSubscribe = async () => {
    try {
      setPaymentProcessing(true);
      const response = await axios.post(
        `${API}/payments/subscription/checkout`,
        { origin_url: window.location.origin },
        axiosConfig
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      setNotification({ type: "error", message: t('common:errors.somethingWentWrong') });
      setPaymentProcessing(false);
    }
  };

  const markNotificationRead = async (notifId) => {
    await axios.put(`${API}/notifications/${notifId}/read`, {}, axiosConfig);
    fetchData();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${API}/payments/subscription/cancel`, {}, axiosConfig);
      setNotification({ type: "success", message: t('common:messages.success') });
      setShowCancelModal(false);
      if (setUser) {
        setUser({ subscription_cancelled: true });
      }
    } catch (error) {
      setNotification({ type: "error", message: error.response?.data?.detail || t('common:errors.somethingWentWrong') });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(`${API}/payments/subscription/reactivate`, {}, axiosConfig);
      setNotification({ type: "success", message: t('common:messages.success') });
      if (setUser) {
        setUser({ subscription_cancelled: false });
      }
    } catch (error) {
      setNotification({ type: "error", message: error.response?.data?.detail || t('common:errors.somethingWentWrong') });
    }
  };

  // Get translated goal type
  const getGoalLabel = (goal) => {
    const goals = {
      customers: t('dashboard:goals.customers'),
      leads: t('dashboard:goals.leads'),
      traffic: t('dashboard:goals.traffic')
    };
    return goals[goal] || goal;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: "badge-success", icon: CheckCircle, label: t('common:status.active') },
      pending_review: { color: "badge-warning", icon: Clock, label: t('common:status.pendingReview') },
      paused: { color: "badge-warning", icon: Clock, label: t('common:status.paused') },
      completed: { color: "badge-info", icon: CheckCircle, label: t('common:status.completed') },
      archived: { color: "bg-gray-500/20 text-gray-400 border border-gray-500/30", icon: Clock, label: t('common:status.archived') },
      cancelled: { color: "badge-purple", icon: XCircle, label: t('common:status.cancelled') },
      rejected: { color: "bg-red-500/20 text-red-400 border border-red-500/30", icon: XCircle, label: t('common:status.rejected') }
    };
    const badge = badges[status] || { color: "bg-gray-500/20 text-gray-400", icon: Clock, label: status || "Unknown" };
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <badge.icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05060D] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060D]">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
              alt="EleKtro"
              className="w-10 h-10"
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white leading-tight">EleKtro Universal</span>
              <span className="text-[10px] text-gray-500 leading-tight">Conscious Creator</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Role Switcher - to Creator */}
            <Link
              to="/creators"
              data-testid="switch-to-creator-btn"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors text-sm"
            >
              <ArrowRightLeft className="w-4 h-4" />
              {t('landing:nav.switchToCreator')}
            </Link>
            
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                data-testid="notifications-btn"
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors relative"
              >
                <Bell className="w-5 h-5" />
                {notifications.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full text-xs flex items-center justify-center text-white font-bold">
                    {notifications.unread_count}
                  </span>
                )}
              </button>
              
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 glass-card rounded-xl shadow-2xl border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-semibold text-white">{t('dashboard:business.notifications.title')}</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">{t('dashboard:business.notifications.empty')}</div>
                    ) : (
                      notifications.notifications.slice(0, 10).map((notif) => (
                        <div 
                          key={notif.notification_id}
                          onClick={() => markNotificationRead(notif.notification_id)}
                          className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 ${!notif.read ? 'bg-cyan-500/5' : ''}`}
                        >
                          <div className="font-medium text-white text-sm">{notif.title}</div>
                          <div className="text-xs text-gray-400 mt-1">{notif.message}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="text-right mr-4">
              <div className="text-sm text-gray-400">{t('dashboard:welcome', { name: '' }).replace(', ', '')}</div>
              <div className="font-semibold text-white">{user?.name}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              title={t('common:nav.logout')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            notification.type === "success" ? "bg-green-500/10 border border-green-500/30 text-green-400" :
            notification.type === "error" ? "bg-red-500/10 border border-red-500/30 text-red-400" :
            notification.type === "warning" ? "bg-yellow-500/10 border border-yellow-500/30 text-yellow-400" :
            "bg-blue-500/10 border border-blue-500/30 text-blue-400"
          }`}>
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{notification.message}</span>
            <button 
              onClick={() => setNotification(null)}
              className="ml-auto hover:opacity-70"
            >
              ×
            </button>
          </div>
        )}

        {/* Subscription Banner */}
        {!user?.is_subscribed && (
          <div className="mb-8 glass-card rounded-2xl p-6 border border-blue-500/30 bg-gradient-to-r from-blue-600/10 to-purple-600/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold font-['Syne'] text-white mb-1">
                  {t('dashboard:business.subscription.subscribeDesc')}
                </h3>
                <p className="text-gray-400">
                  {t('dashboard:business.subscription.price')} • {t('common:status.cancelled').toLowerCase() === 'cancelled' ? 'Cancel anytime' : t('common:actions.cancel') + ' ' + t('dashboard:time.allTime')}
                </p>
              </div>
              <button
                onClick={handleSubscribe}
                disabled={paymentProcessing}
                data-testid="subscribe-btn"
                className="btn-primary px-8 py-3 rounded-xl font-bold text-white flex items-center gap-2 disabled:opacity-50"
              >
                {paymentProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t('common:actions.loading')}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    {t('dashboard:business.subscription.subscribe')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-['Unbounded'] text-white mb-2">
              {t('dashboard:business.title')}
            </h1>
            <p className="text-gray-400 text-sm md:text-base">
              {t('dashboard:business.stats.trustLine')}
            </p>
          </div>
          {user?.is_subscribed && (
            <button
              onClick={() => navigate("/dashboard/campaigns/new")}
              data-testid="create-campaign-btn"
              className="btn-primary px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('dashboard:business.campaigns.create')}
            </button>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { 
              label: t('dashboard:business.stats.activeCampaigns'), 
              value: analytics?.active_campaigns || 0, 
              icon: Calendar,
              color: "cyan" 
            },
            { 
              label: t('dashboard:business.stats.totalCampaigns'), 
              value: analytics?.total_campaigns || 0, 
              icon: BarChart3,
              color: "blue" 
            },
            { 
              label: t('dashboard:business.stats.totalCreators'), 
              value: analytics?.total_participants || 0, 
              icon: Users,
              color: "purple" 
            },
            { 
              label: t('dashboard:business.stats.totalSpent'), 
              value: `€${analytics?.total_budget_spent || 0}`, 
              icon: DollarSign,
              color: "green" 
            }
          ].map((stat, index) => (
            <div key={`stat-${index}`} className="glass-card rounded-xl p-5 card-hover">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                stat.color === "cyan" ? "bg-cyan-500/20" :
                stat.color === "blue" ? "bg-blue-500/20" : 
                stat.color === "green" ? "bg-green-500/20" : "bg-purple-500/20"
              }`}>
                <stat.icon className={`w-5 h-5 ${
                  stat.color === "cyan" ? "text-cyan-400" :
                  stat.color === "blue" ? "text-blue-400" : 
                  stat.color === "green" ? "text-green-400" : "text-purple-400"
                }`} />
              </div>
              <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-xs text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Trust Line */}
        <div className="mb-8 text-center">
          <p className="text-gray-400 text-sm">
            {t('dashboard:business.stats.trustLine')}
          </p>
        </div>

        {/* Subscription Status */}
        <div className="mb-8">
          <div className="glass-card rounded-xl p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  user?.is_subscribed ? (user?.subscription_cancelled ? "bg-yellow-500/20" : "bg-green-500/20") : "bg-gray-500/20"
                }`}>
                  <DollarSign className={`w-6 h-6 ${
                    user?.is_subscribed ? (user?.subscription_cancelled ? "text-yellow-400" : "text-green-400") : "text-gray-400"
                  }`} />
                </div>
                <div>
                  <div className="font-semibold text-white">{t('dashboard:business.subscription.title')}</div>
                  <div className="text-sm text-gray-400">
                    {user?.is_subscribed 
                      ? user?.subscription_cancelled
                        ? t('dashboard:business.subscription.cancelledUntil', { date: new Date(user.subscription_end_date).toLocaleDateString() })
                        : t('dashboard:business.subscription.expires', { date: new Date(user.subscription_end_date).toLocaleDateString() })
                      : t('common:status.pending')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  user?.is_subscribed 
                    ? user?.subscription_cancelled 
                      ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" 
                      : "badge-success" 
                    : "bg-gray-500/20 text-gray-400"
                }`}>
                  {user?.is_subscribed 
                    ? user?.subscription_cancelled ? t('dashboard:business.subscription.cancelled') : t('dashboard:business.subscription.active') 
                    : t('common:status.pending')}
                </span>
                {user?.is_subscribed && !user?.subscription_cancelled && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
                    data-testid="cancel-subscription-btn"
                  >
                    {t('dashboard:business.subscription.cancel')}
                  </button>
                )}
                {user?.is_subscribed && user?.subscription_cancelled && (
                  <button
                    onClick={handleReactivateSubscription}
                    className="px-4 py-2 text-sm text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/10 transition-colors"
                    data-testid="reactivate-subscription-btn"
                  >
                    {t('dashboard:business.subscription.reactivate')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Cancel Subscription Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="glass-card rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">{t('dashboard:business.cancelModal.title')}</h3>
                <button 
                  onClick={() => setShowCancelModal(false)}
                  className="p-1 text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-gray-300 mb-4">
                {t('dashboard:business.cancelModal.description')}
              </p>
              <ul className="text-sm text-gray-400 mb-6 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  {t('dashboard:business.cancelModal.keepAccess', { date: user?.subscription_end_date ? new Date(user.subscription_end_date).toLocaleDateString() : '' })}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  {t('dashboard:business.cancelModal.preserveData')}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  {t('dashboard:business.cancelModal.reactivateAnytime')}
                </li>
              </ul>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 py-3 rounded-xl border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
                >
                  {t('dashboard:business.cancelModal.cancel')}
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                  className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50"
                  data-testid="confirm-cancel-btn"
                >
                  {cancelLoading ? t('common:actions.loading') : t('dashboard:business.cancelModal.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Click Trends Chart (Simple) */}
        {analytics?.clicks_by_day && Object.keys(analytics.clicks_by_day).length > 0 && (
          <div className="glass-card rounded-xl p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              <h3 className="font-semibold text-white">{t('dashboard:business.stats.totalResults')} (7d)</h3>
            </div>
            <div className="flex items-end gap-2 h-32">
              {Object.entries(analytics.clicks_by_day)
                .sort(([a], [b]) => a.localeCompare(b))
                .slice(-7)
                .map(([date, clicks]) => {
                  const maxClicks = Math.max(...Object.values(analytics.clicks_by_day));
                  const height = maxClicks > 0 ? (clicks / maxClicks) * 100 : 0;
                  return (
                    <div key={date} className="flex-1 flex flex-col items-center gap-1">
                      <div 
                        className="w-full bg-cyan-500/30 rounded-t transition-all hover:bg-cyan-500/50"
                        style={{ height: `${Math.max(height, 4)}%` }}
                      ></div>
                      <span className="text-[10px] text-gray-500">
                        {new Date(date).toLocaleDateString(undefined, { weekday: 'short' })}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Campaigns Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{t('dashboard:business.campaigns.title')}</h2>
          </div>
          
          {campaigns.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">{t('dashboard:business.campaigns.empty')}</h3>
              <p className="text-gray-400 text-sm mb-6">
                {t('dashboard:business.campaigns.emptyDesc')}
              </p>
              {user?.is_subscribed && (
                <button
                  onClick={() => navigate("/dashboard/campaigns/new")}
                  className="btn-primary px-6 py-3 rounded-xl font-bold text-white inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  {t('dashboard:business.campaigns.emptyButton')}
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {campaigns.filter(c => c.status !== 'deleted' && c.status !== 'archived' && c.status !== 'cancelled').map((campaign) => (
                <div 
                  key={campaign.campaign_id}
                  onClick={() => navigate(`/dashboard/campaigns/${campaign.campaign_id}`)}
                  className="glass-card rounded-xl p-6 card-hover cursor-pointer"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white">{campaign.title}</h3>
                        {getStatusBadge(campaign.status)}
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-1">{campaign.description}</p>
                      <div className="flex items-center gap-4 mt-3">
                        <span className="text-xs text-gray-500">
                          €{campaign.cost_per_result} {t('dashboard:business.campaigns.perResult', { goalType: getGoalLabel(campaign.goal) })}
                        </span>
                        <span className="text-xs text-gray-500">
                          €{campaign.budget_cap} {t('dashboard:business.campaigns.budget')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">{campaign.total_clicks || 0}</div>
                        <div className="text-xs text-gray-500">{t('dashboard:business.stats.totalResults')}</div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Archived Campaigns Section */}
        {(archivedCampaigns.length > 0 || campaigns.filter(c => c.status === 'deleted' || c.status === 'archived').length > 0) && (
          <div className="mb-8">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
              data-testid="toggle-archived-btn"
            >
              <Archive className="w-4 h-4" />
              <span className="text-sm">{t('dashboard:business.campaigns.archived.title')}</span>
              <span className="text-xs text-gray-500">
                - {t('dashboard:business.campaigns.archived.count', { count: archivedCampaigns.length + campaigns.filter(c => c.status === 'deleted' || c.status === 'archived').length })}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showArchived ? 'rotate-180' : ''}`} />
            </button>
            
            {showArchived && (
              <div className="grid gap-4 opacity-60">
                {[...archivedCampaigns, ...campaigns.filter(c => c.status === 'deleted' || c.status === 'archived')].map((campaign) => (
                  <div 
                    key={campaign.campaign_id}
                    className="glass-card rounded-xl p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-white">{campaign.title}</h3>
                          {getStatusBadge(campaign.status)}
                        </div>
                        <p className="text-gray-400 text-sm line-clamp-1">{campaign.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Help Assistant */}
      <AIHelpAssistant />
    </div>
  );
};

export default BusinessDashboard;
